/**
 * Color Variants Web Components for Shopify Horizon theme.
 *
 * ColorVariantsComponent – swatch row + "see all" trigger
 * ColorVariantsDrawer    – dialog panel with full colour grid
 */

class ColorVariantsComponent extends HTMLElement {
  /** @type {HTMLImageElement | null} */
  #previewImg = null;
  #origSrc = '';
  #origSrcset = '';

  connectedCallback() {
    this.querySelector('[data-open-drawer]')?.addEventListener('click', () => {
      this.querySelector('color-variants-drawer')?.showDialog();
    });

    this.querySelectorAll('.cv-swatch[data-preview-img]').forEach((swatch) => {
      swatch.addEventListener('pointerenter', () => this.#previewSwatch(swatch));
      swatch.addEventListener('pointerleave', () => this.#resetPreview());
    });
  }

  /** @param {Element} swatch */
  #previewSwatch(swatch) {
    const imgUrl = /** @type {HTMLElement} */ (swatch).dataset.previewImg;
    if (!imgUrl) return;

    // Look within the same section first, fall back to entire document
    const scope = this.closest('.shopify-section') ?? document;
    const img =
      scope.querySelector('media-gallery .product-media__image') ??
      scope.querySelector('.product-media__image');
    if (!(img instanceof HTMLImageElement)) return;

    this.#previewImg = img;
    this.#origSrc = img.src;
    this.#origSrcset = img.srcset;
    img.src = imgUrl;
    img.srcset = '';
  }

  #resetPreview() {
    if (!this.#previewImg) return;
    this.#previewImg.src = this.#origSrc;
    this.#previewImg.srcset = this.#origSrcset;
    this.#previewImg = null;
  }
}

class ColorVariantsDrawer extends HTMLElement {
  /** @type {HTMLDialogElement | null} */
  #dialog = null;
  /** @type {HTMLImageElement | null} */
  #previewImg = null;
  #origSrc = '';
  #origSrcset = '';
  #prevScrollY = 0;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;

    // Close button
    this.querySelector('[data-close-drawer]')?.addEventListener('click', () => this.closeDialog());

    // On mobile: close when clicking the backdrop outside the panel
    this.#dialog.addEventListener('click', (e) => {
      if (!this.#dialog || window.innerWidth >= 990) return;
      const rect = this.#dialog.getBoundingClientRect();
      const outside =
        e.clientX < rect.left ||
        e.clientX > rect.right ||
        e.clientY < rect.top ||
        e.clientY > rect.bottom;
      if (outside) this.closeDialog();
    });

    // Escape key
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#dialog?.open) {
        e.preventDefault();
        this.closeDialog();
      }
    });

    // Desktop: hovering a card previews in the main gallery
    this.querySelectorAll('[data-preview-img]').forEach((card) => {
      card.addEventListener('pointerenter', () => this.#previewCard(card));
      card.addEventListener('pointerleave', () => this.#resetPreview());
    });
  }

  showDialog() {
    if (!this.#dialog || this.#dialog.open) return;

    const isDesktop = window.innerWidth >= 990;

    if (isDesktop) {
      // Size drawer to span from the product info column's left edge to the right
      const component = this.closest('color-variants-component');
      if (component) {
        const left = component.getBoundingClientRect().left;
        this.#dialog.style.width = `${window.innerWidth - left}px`;
      }
      // Non-modal on desktop: no backdrop, no scroll lock, gallery stays interactive
      this.#dialog.show();
    } else {
      this.#dialog.style.width = '';
      // Modal on mobile: full-screen with scroll lock
      this.#prevScrollY = window.scrollY;
      requestAnimationFrame(() => {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.#prevScrollY}px`;
        this.#dialog?.showModal();
      });
    }
  }

  closeDialog() {
    if (!this.#dialog?.open) return;

    this.#resetPreview();

    const isDesktop = window.innerWidth >= 990;

    this.#dialog.style.animation = 'none';
    void this.#dialog.offsetWidth;
    this.#dialog.classList.add('dialog-closing');
    this.#dialog.style.animation = '';

    this.#dialog.addEventListener(
      'animationend',
      () => {
        if (!isDesktop) {
          document.body.style.width = '';
          document.body.style.position = '';
          document.body.style.top = '';
          window.scrollTo({ top: this.#prevScrollY, behavior: 'instant' });
        }
        this.#dialog?.close();
        this.#dialog?.classList.remove('dialog-closing');
      },
      { once: true }
    );
  }

  /** @param {Element} card */
  #previewCard(card) {
    if (window.innerWidth < 990) return;

    const imgUrl = /** @type {HTMLElement} */ (card).dataset.previewImg;
    if (!imgUrl) return;

    const scope = this.closest('.shopify-section') ?? document;
    const img =
      scope.querySelector('media-gallery .product-media__image') ??
      scope.querySelector('.product-media__image');
    if (!(img instanceof HTMLImageElement)) return;

    this.#previewImg = img;
    this.#origSrc = img.src;
    this.#origSrcset = img.srcset;
    img.src = imgUrl;
    img.srcset = '';
  }

  #resetPreview() {
    if (!this.#previewImg) return;
    this.#previewImg.src = this.#origSrc;
    this.#previewImg.srcset = this.#origSrcset;
    this.#previewImg = null;
  }
}

if (!customElements.get('color-variants-component')) {
  customElements.define('color-variants-component', ColorVariantsComponent);
}

if (!customElements.get('color-variants-drawer')) {
  customElements.define('color-variants-drawer', ColorVariantsDrawer);
}
